import { AbilityBuilder, createMongoAbility } from '@casl/ability'
import { map, get } from 'lodash'
import AccountMembershipRoles from '@constants/account_membership_roles.ts'
import { createLogger } from '@utils/logger.ts'

const log = createLogger('ability.js', { prependScope: true })
log.setLevel('silent')

/**
 * Note: Casl is best used for simple attribute-based authorization.
 * If you need to do advanced checks, you might want to pass in multiple objects
 * and then create attributes from those
 *
 * @param user contains details about logged in user: its id, name, email, etc
 * @param extras an optional object with additional objects that may enable additional rules etc
 */
export default function defineAbilityFor(user, extras = {}) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility)

  if (user) {
    let accountIds = []
    if (user.current_account_membership !== undefined) {
      accountIds = map(user.account_memberships, accountMembership => accountMembership.account_id)
    }

    // user
    const numUnpublishedTimelinesAvailable = get(user, 'num_unpublished_timelines_available')

    // account membership
    const currentAccountMembership = get(user, ['current_account_membership'])
    const isAccountOwner = AccountMembershipRoles.OWNER === get(currentAccountMembership, 'role_id')
    const memberPublishingVal = get(currentAccountMembership, ['publishing'])

    // account prefs
    const currentAccountPublishingVal = get(user, ['current_account_allows_members_to_publish'])

    if (true) {
      //log.debug('user', user)
      //log.debug('accountIds', accountIds)
      log.debug('memberPublishingVal', memberPublishingVal)
      log.debug('currentAccountPublishingVal', currentAccountPublishingVal)
      log.debug('numUnpublishedTimelinesAvailable', numUnpublishedTimelinesAvailable)
      log.debug('------------------')
    }

    // General publishing ability, affected by account ownership, member specific publishing value
    // and account-level publishing value
    let canPublish = false
    if (isAccountOwner) {
      canPublish = true
    } else {
      // Note: memberPublishingVal value may be null, in which case we defer to account-level prefs
      canPublish = memberPublishingVal === null ? currentAccountPublishingVal : memberPublishingVal
    }
    if (canPublish) {
      can('publish')
    }
    // Check if user can still add draft timelines
    if (numUnpublishedTimelinesAvailable > 0) {
      can('stillAddDraftTimelines')
    }

    // Private timelines feature
    const canCreatePrivate = get(user, 'can_create_private')
    if (canCreatePrivate) {
      can('createPrivate')
      can('makePrivate', 'Resource', { owner_id: user.id }) // when resource owner
      can('makePrivate', 'Resource', { id: -1 }) // when new resource
    }

    // create groups

    // groups feature ability
    const groupsFeatureAbility = get(user, 'groups_feature_ability')
    if (groupsFeatureAbility) {
      can('groups_feature_ability')
    }

    // create groups ability (may not be available to all users even if they have access to groups feature)
    const canCreateGroups = get(user, 'can_create_groups')
    if (canCreateGroups) {
      can('createGroups')
      can('create', 'Group')
    }

    /*  
    // Note: Many access actions are difficult to check using CASL's attribute-based access control model.
    // You can use the access api to check abilities on an ad-hoc basis.
    // These examples are only provided as a guide

    // Timelines
    // lock down private timelines, we can open up later
    cannot(['read', 'edit', 'update', 'delete'], 'Timeline', { access_level: InternalAccessLevel.PRIVATE } )
    // allow when internal access level is public and on same account
    if (accountIds.length > 0) {
      can(['read', 'edit', 'update', 'delete'], 'Timeline', { account_id: { $in: accountIds }, access_level: InternalAccessLevel.PUBLIC })
    }
    // finally, allow when timeline owner
    can(['read', 'edit', 'update', 'delete'], 'Timeline', { owner_id: user.id })
  */

    /*
    // enable additional rules for when additional objects are passed in
    if(Object.keys(extras).length > 0){
      const { someExtraObject } = extras
      if(someExtraObject){
        // potentially enable additional rules
      }
    }
  */
  } else {
    //console.warn("Defined ability without user")
  }
  return build()
}
